import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "cyber-defense",
  kind: "horizontal",
  title: "Cyber Defense & Perimeter Engineering",
  shortTitle: "Cyber Defense",
  status: "draft",
  tier: "experience",
  seoTitle: "Cyber Defense & Perimeter Engineering | General Applications",
  seoDescription:
    "Perimeter defense, network segmentation, electronic security perimeters, intermediate jumpboxes, and access point hardening.",
  summary:
    "Defense engineering for critical networks at two connected levels: knowing the actual device baseline and making every path between devices deliberate, visible, and constrained.",
  image: "/images/blog/mapping-the-undocumented-network-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["military", "enterprise", "energy"],
  relatedPostSlugs: [
    "hardening-mission-critical-firewalls",
    "mapping-the-undocumented-network",
    "vulnerability-triage-operational-hardware",
    "air-gap-is-a-myth",
  ],
  engineering: [
    "Firewall rulebase reduction and source, destination, and port justification",
    "Physical and logical network diagrams that reconcile field reality with configuration records",
    "Multi-source asset inventory normalization and unknown-device discovery",
    "Device-level configuration, open-port, and running-service baselining",
    "Fielded-equipment vulnerability assessment and safe CVE triage",
    "Electronic security perimeter and enclave boundary isolation",
    "Access-point hardening and intermediate-system design",
  ],
  phases: {
    study: {
      focus:
        "Establish what devices exist, how they connect, and which paths are actually permitted before changing the network.",
      produces: [
        "Physical and logical asset-boundary inventory",
        "Management-feed reconciliation and unknown-device analysis",
        "Firewall-rule and unverified-access-point audit",
        "Device-baseline and vulnerability-evaluation scope",
      ],
      fit: {
        good: [
          "Operational networks with undocumented paths, legacy rules, or incomplete inventory",
          "Teams that need device and network evidence before approving a defensive change",
        ],
        poor: [
          "Managed antivirus, helpdesk monitoring, or commodity endpoint procurement",
          "A network where ownership of devices and business flows cannot yet be identified",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the device baseline, network perimeter, and access boundaries as one defensive model.",
      produces: [
        "Electronic security perimeter and enclave architecture",
        "Canonical asset and configuration schema",
        "Least-privilege traffic-flow and rule-justification model",
        "Access-point and intermediate-system boundary design",
      ],
    },
    "detailed-design": {
      focus:
        "Specify verifiable device and network controls that can be changed safely on running operational systems.",
      produces: [
        "Device-class baseline checklists for ports, services, and configuration",
        "Firewall migration, pruning, rollback, and verification procedures",
        "Vulnerability-triage criteria grounded in the fielded configuration",
        "Network-diagram and configuration-registry maintenance rules",
      ],
    },
    implementation: {
      focus:
        "Apply and verify perimeter and device changes without losing the evidence needed to operate or defend the network afterward.",
      produces: [
        "Validated firewall rulebases with accountable business ownership",
        "Authoritative network diagrams linked to asset and configuration records",
        "Device inventory with normalized hardware, network, and service baselines",
        "Defensive verification records for subsequent operations and audits",
      ],
    },
  },
};
